/**
 * The plain-form links shown when the assistant is unavailable.
 *
 * Deliberately simple text links, not buttons: they are the way round a chat
 * that is down, not an alternative front door competing with it. When they
 * show is decided by `showsFallbackLinks` in `assistant-fallback.ts`.
 */

import { cn } from "@norican/react";
import { Link } from "react-router";

import { useT } from "@/i18n";
import { useKiosk } from "@/lib/api";
import { logger } from "@/lib/logger";

import { ASSISTANT_FALLBACK_LINKS } from "./assistant-fallback";

export interface AssistantFallbackLinksProps {
  className?: string;
}

export function AssistantFallbackLinks({ className }: AssistantFallbackLinksProps) {
  const { t } = useT("assistant");
  // A shared device has no "My tickets" to go to (1 Oct).
  const kiosk = useKiosk();
  const links = ASSISTANT_FALLBACK_LINKS.filter(
    (link) => kiosk === null || link.key !== "myTickets",
  );

  return (
    <nav aria-label={t("fallback.label")} className={cn(className)}>
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {links.map((link) => (
          <li key={link.key}>
            <Link
              to={link.path}
              className="text-accent-fg-subtle font-medium underline-offset-2 hover:underline"
              onClick={() => logger.debug("[assistant] fallback link", link.key)}
            >
              {t(`fallback.${link.key}`)}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
